import sanitizeHtml from 'sanitize-html';
import { Extension } from '@tiptap/core';
import { v4 as uuid } from 'uuid';

import {
	Plugin,
	PluginKey,
	TextSelection,
	AllSelection
} from 'prosemirror-state';

import { Color } from '@tiptap/extension-color';
import Paragraph from '@tiptap/extension-paragraph';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import UniqueId from 'tiptap-unique-id';
import Focus from '@tiptap/extension-focus';
import History from '@tiptap/extension-history';
import TextAlign from '@tiptap/extension-text-align';

const LineHeight = Extension.create({
	name: 'lineHeight',

	addGlobalAttributes() {
		return [
			{
				types: ['paragraph', 'heading'],
				attributes: {
					lineHeight: {
						default: null,

						parseHTML: (element) => {
							const lh = element.style.lineHeight;
							if (!lh) {return null;}

							if (lh.endsWith('px')) {
								const pxValue = parseFloat(lh);
								const fontSize = parseFloat(window.getComputedStyle(element).fontSize || '16');
								const ratio = +(pxValue / fontSize).toFixed(2);
								return ratio.toString();
							}

							if (lh.endsWith('%')) {
								const percentValue = parseFloat(lh);
								const ratio = +(percentValue / 100).toFixed(2);
								return ratio.toString();
							}

							return lh;
						},

						renderHTML: (attributes) => {
							if (!attributes.lineHeight) {return {};}
							return {
								style: `line-height: ${attributes.lineHeight}`,
							};
						},
					},
				},
			},
		];
	},

	addCommands() {
		return {
			setLineHeight: (value) => ({ commands }) => {
				return (
					commands.updateAttributes('paragraph', { lineHeight: value }) ||
					commands.updateAttributes('heading', { lineHeight: value })
				);
			},
			unsetLineHeight: () => ({ commands }) => {
				return (
					commands.resetAttributes('paragraph', 'lineHeight') ||
					commands.resetAttributes('heading', 'lineHeight')
				);
			},
		};
	},
});

const CustomParagraph = Paragraph.extend({
	addAttributes() {
		return {
			id: {
				default: null,
				parseHTML: (element) => {
					return element.hasAttribute('id')
						? element.getAttribute('id')
						: null;
				},
			},
			class: {
				parseHTML: (element) => {
					return element.hasAttribute('class')
						? element.getAttribute('class')
						: null;
				},
			}
		};
	},
});

const CustomFontSize = Extension.create({
	name: 'fontSize',
	addOptions: function addOptions() {
		return {
			types: ['textStyle'],
		};
	},
	addGlobalAttributes: function addGlobalAttributes() {
		return [
			{
				types: this.options.types,
				attributes: {
					fontSize: {
						default: null,
						parseHTML: function parseHTML(element) {
							return element.style.fontSize?.replace(
								/['"]+/g,
								''
							);
						},
						renderHTML: function renderHTML(attributes) {
							if (!attributes.fontSize) {
								return {};
							}

							return {
								style: `font-size: ${  attributes.fontSize}`,
							};
						},
					},
				},
			},
		];
	},
    
	addCommands: function addCommands() {
		return {
			setFontSize: function setFontSize(fontSize) {
				return function (_ref) {
					const chain = _ref.chain;
					return chain()
						.setMark('textStyle', {
							fontSize: fontSize,
						})
						.run();
				};
			},
			unsetFontSize: function unsetFontSize() {
				return function (_ref2) {
					const chain = _ref2.chain;
					return chain()
						.setMark('textStyle', {
							fontSize: null,
						})
						.removeEmptyTextStyle()
						.run();
				};
			},
		};
	},
});

const NoNewLine = Extension.create({
	name: 'no_new_line',

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey('eventHandler'),
				props: {
					handleKeyDown: (view, event) => {
						if ((event.key === 'Enter' && !event.shiftKey) && this.options.disabled) {
							return true;
						}
					}
				},
			}),
		];
	},
});

export const Clipboard = Extension.create({
	name: 'clipboard',
 
	addCommands() {
		return {
			cut: () => async ({ tr, dispatch, editor }) => {
				const selection = editor.state.selection;
				const selectedText =  editor.state.doc.textBetween(selection.from, selection.to, ' ');

				try {
					await navigator.clipboard.writeText(selectedText);
				} catch {
					return false;
				}

				editor.commands.deleteSelection();

				if (tr.docChanged) {
					dispatch && dispatch(tr);
					return true;
				}

				editor.chain().focus().run();

				return false;
			},
			copy: () => async ({ tr, dispatch, editor }) => {
				const selection = editor.state.selection;
				const selectedText =  editor.state.doc.textBetween(selection.from, selection.to, ' ');

				try {
					await navigator.clipboard.writeText(selectedText);
				} catch {
					return false;
				}

				if (tr.docChanged) {
					dispatch && dispatch(tr);
					return true;
				}

				editor.chain().focus().run();

				return false;
			},
			paste: () => async ({ editor }) => {
				let text;
				try {
					text = await navigator.clipboard.readText();
				} catch {
					return false;
				}

				// The document may have changed while the clipboard permission prompt was open,
				// so the position must come from the live editor state, not the pre-await one.
				if (editor.isDestroyed) {
					return false;
				}

				editor.commands.insertContentAt(editor.state.selection.from, text);
				editor.chain().focus().run();

				return true;
			}
		};
	}
});

export const getPasteHTMLConfig = (html, fullEditor) => {
	return (html) => {
		if (fullEditor) {
			return sanitizeHtml(html, {
				allowedSchemes: ['http', 'https', 'mailto', 'tel'],
			});
		}
	};
};

export const clamp = (val, min, max) => {
	if (val < min) {
		return min;
	}
    
	if (val > max) {
		return max;
	}
    
	return val;
};

const IndentProps = {
	min: 0,
	max: 210,

	more: 30,
	less: -30,
	default: 'default'
};

export function isBulletListNode(node) {
	return node.type.name === 'bullet_list';
}

export function isOrderedListNode(node) {
	return node.type.name === 'order_list';
}

export function isTodoListNode(node) {
	return node.type.name === 'todo_list';
}

export function isListNode(node) {
	return isBulletListNode(node) ||
        isOrderedListNode(node) ||
        isTodoListNode(node);
}

function setNodeIndentMarkup(tr, pos, delta) {
	if (!tr.doc) {return tr;}

	const node = tr.doc.nodeAt(pos);
	if (!node) {return tr;}

	const minIndent = IndentProps.min;
	const maxIndent = IndentProps.max;

	const indent = delta === 'default' ? 0 : clamp(
		(node.attrs.indent || 0) + delta,
		minIndent,
		maxIndent,
	);

	if (indent === node.attrs.indent) {return tr;}

	const nodeAttrs = {
		...node.attrs,
		indent,
	};

	return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
}

const updateIndentLevel = (tr, delta) => {
	const { doc, selection } = tr;

	if (!doc || !selection) {return tr;}

	if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
		return tr;
	}

	const { from, to } = selection;

	doc.nodesBetween(from, to, (node, pos) => {
		const nodeType = node.type;

		if (nodeType.name === 'paragraph' || nodeType.name === 'heading') {
			tr = setNodeIndentMarkup(tr, pos, delta);
			return false;
		}
        
		return !isListNode(node);
	});

	return tr;
};

export const Indent = Extension.create({
	name: 'indent',

	addOptions() {
		return {
			allowIndentation: false,
			types: ['heading', 'paragraph'],
			indentLevels: [0, 30, 60, 90, 120, 150, 180, 210],
			defaultIndentLevel: 0,
		};
	},

	addGlobalAttributes() {
		return [
			{
				types: this.options.types,
				attributes: {
					indent: {
						default: this.options.defaultIndentLevel,
						renderHTML: (attributes) => ({
							style: `margin-left: ${attributes.indent}px !important;`
						}),
						parseHTML: (element) => parseInt(element.style.marginLeft) || this.options.defaultIndentLevel,
					},
				},
			},
		];
	},

	addCommands() {
		return {
			indent: () => ({ tr, state, dispatch, editor }) => {
				if (this.options.allowIndentation) {
					const { selection } = state;
                    
					tr = tr.setSelection(selection);
					tr = updateIndentLevel(tr, IndentProps.more);
    
					if (tr.docChanged) {
						dispatch && dispatch(tr);
						return true;
					}
    
					editor.chain().focus().run();
    
					return false;
				}
			},
			outdent: () => ({ tr, state, dispatch, editor }) => {
				if (this.options.allowIndentation) {
					const { selection } = state;
					tr = tr.setSelection(selection);
					tr = updateIndentLevel(tr, IndentProps.less);

					if (tr.docChanged) {
						dispatch && dispatch(tr);
						return true;
					}

					editor.chain().focus().run();

					return false;
				}
			},
			resetIndentation: () => ({ tr, state, dispatch, editor }) => {
				if (this.options.allowIndentation) {
					const { selection } = state;

					tr = tr.setSelection(selection);
					tr = updateIndentLevel(tr, IndentProps.default);

					if (tr.docChanged) {
						dispatch && dispatch(tr);
						return true;
					}

					editor.chain().focus().run();

					return false;
				}
			},
		};
	},
    
	addKeyboardShortcuts() {
		return {
			Tab: () => { if (!(this.editor.isActive('bulletList') || this.editor.isActive('orderedList'))) {return this.editor.commands.indent();} },
			'Shift-Tab': () => { if (!(this.editor.isActive('bulletList') || this.editor.isActive('orderedList'))) {return this.editor.commands.outdent();} },
			Backspace: () => { if (!(this.editor.isActive('bulletList') || this.editor.isActive('orderedList'))) {return this.editor.commands.resetIndentation();} },
		};
	},
});

const getEditorExtensions = (
	allowMultipleLines,
	fullEditor,
	placeholder,
	maxLength,
	allowIndentation = false,
	isPreview = false
) => {
	return [
		UniqueId.configure({
			attributeName: 'id',
			types: ['bulletList', 'listItem'],
			createId: () => `highlight-${uuid()}`,
		}),
		NoNewLine.configure({ disabled: !allowMultipleLines && !fullEditor }),
		Document,
		History.configure({
			depth: 10,
		}),
		TextAlign.configure({
			types: ['heading', 'paragraph'],
		}),
		Text,
		LineHeight,
		Color,
		TextStyle.configure({ mergeNestedSpanStyles: true }),
		FontFamily,
		Bold,
		CustomFontSize,
		Italic,
		OrderedList,
		ListItem,
		Underline,
		Clipboard,
		Focus.configure({
			className: allowIndentation ? 'editor-focused' : '',
		}),
		Indent.configure({
			allowIndentation
		}),
		BulletList.configure({
			keepMarks: true,
			keepAttributes: true,
			HTMLAttributes: {
				class: 'highlight-bp',
			},
		}),
		Link.configure({
			protocols: ['http', 'mailto'],
			openOnClick: false,
			HTMLAttributes: {
				class: 'underline decoration-[#2A8176]',
			},
		}),
		CustomParagraph,
		Placeholder.configure({
			placeholder: isPreview ? '' : placeholder,
			emptyNodeClass: 'is-empty h-max first:before:text-gray-400 first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none',
		}),
		CharacterCount.configure({
			limit: maxLength,
		}),
	];
};

export default getEditorExtensions;
